/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useReducer } from "react"
import { useOutletContext, useParams, useNavigate } from "react-router-dom"
import { isAxiosError } from "axios"
import { axiosErrorToStringError } from "@/utils/axiosErrorToStringError"
import { Save, Hourglass } from "lucide-react"
import { useGetForum } from "@/services/forumService"
import { useCreateTopic } from "@/services/topicService"
import ForumHeader from "@/components/ForumHeader"
import ErrorMessage from "@/components/ErrorMessage"


const initialState = {
    forum: null,
    errorMessage: "",
    apiPayload: {
        title: "",
        status: "draft",
        pinned: false,
        no_response: false,
    },
    isLoading: false,
}



const ACTIONS = {
    SET_FORUM: "setForum",
    SET_ERROR_MESSAGE: "setErrorMessage",
    SET_IS_LOADING: "setIsLoading",

    SET_API_PAYLOAD: "setapiPayload",
}



const reducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.SET_FORUM:
            return { ...state, forum: action.payload }
        case ACTIONS.SET_ERROR_MESSAGE:
            return { ...state, errorMessage: action.payload }
        case ACTIONS.SET_IS_LOADING:
            return { ...state, isLoading: action.payload }
        case ACTIONS.SET_API_PAYLOAD:
            return { ...state, apiPayload: { ...state.apiPayload, [action.subtype]: action.payload } }
        default:
            throw new Error("Unknown action.type " + action.type);
    }
}



export default function Index() {

    const {forum_id} = useParams()
    const getForum = useGetForum()
    const createTopic = useCreateTopic()

    const [ , setPageTitle,  , , , setShowTitle] = useOutletContext()
    const [state, dispatch] = useReducer(reducer, initialState)

    const navigate = useNavigate()


    /* On supprime le titre */
    useEffect(() => {
        setPageTitle("Création d'un sujet")
        setShowTitle(false)

        return () => {
            setPageTitle("Titre")
            setShowTitle(true)
        }
    }, [setPageTitle, setShowTitle])



    /* On change le titre quand le topic est créé */
    useEffect(() => {
        if (state?.topic != null) {
            setPageTitle(state?.topic?.title)
        }
    }, [state?.topic])



    /* Récupération des informations du forum */
    useEffect(() => {
        const localGetForum = async () => {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });
            dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });

            try {
                const forum = await getForum(forum_id)
                dispatch({ type: ACTIONS.SET_FORUM, payload: forum });

            } catch (error) {
                console.error('forums/[forum_id]::getForum::error', error)
                if (isAxiosError(error)) {
                    dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
                } else {
                    dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
                }
            } finally {
                dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
            }
        }

        localGetForum()
    }, [forum_id])



    /* Handler de création du sujet */
    const handleCreateTopic = async () => {

        if (state.apiPayload.title == "") {
            dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: "Veuillez renseigner un titre pour le sujet" })
            return
        }

        dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });
        dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });

        try {
            const topic = await createTopic(forum_id, state.apiPayload)
            
            if (topic.id) 
                navigate(`/forums/${forum_id}/topics/${topic.id}`)
            else
                navigate(`/forums/${forum_id}`)

        } catch (error) {
            console.error('forums/[forum_id]::createTopic::error', error)
            if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
        }
    }


    return (
        <>
            <ErrorMessage errorMessage={state?.errorMessage} />
            <ForumHeader forum={state?.forum} />

            { /* Renseignement du sujet si il est null */ }
            { state?.topic == null && (
                <>
                    <div className="w-full flex flex-col gap-1">
                        {/* Titre du sujet*/}
                        <div className="flex flex-row w-[50%]">
                            <div className="flex flex-col gap-1 mb-5 w-full">
                                <label className="text-secondary" htmlFor="title">Titre du sujet <span className="text-red-600">*</span></label>
                                <input type="text" name="title" 
                                    className="flex-auto rounded-md border border-gray-300 px-3 py-2 text-primary text-base" 
                                    placeholder="Veuillez renseigner un titre pour le sujet"
                                    value={state.apiPayload.title} 
                                    onChange={(e) => dispatch({ type: ACTIONS.SET_API_PAYLOAD, payload: e.target.value, subtype: "title" })} 
                                />
                            </div>
                        </div>

                        {/* Checkbox : Pinned */}
                        <div className="flex flex-row w-full">
                            <div className="flex flex-row items-center gap-2 mb-5 w-full">
                                <input type="checkbox" name="pinned" id="pinned"
                                    className="rounded-md border border-gray-300 w-6 h-6 text-sm" 
                                    checked={state.apiPayload.pinned} 
                                    onChange={(e) => dispatch({ type: ACTIONS.SET_API_PAYLOAD, payload: e.target.checked, subtype: "pinned" })} 
                                />
                                <label className="text-primary" htmlFor="pinned" lang="fr">Sujet épinglé (en haut de la liste)</label>
                            </div>
                        </div>

                        {/* Checkbox : no_response */}
                        <div className="flex flex-row w-full">
                            <div className="flex flex-row items-center gap-2 mb-5 w-full">
                                <input type="checkbox" name="no_response" id="no_response"
                                    className="rounded-md border border-gray-300 w-6 h-6 text-sm" 
                                    checked={state.apiPayload.no_response} 
                                    onChange={(e) => dispatch({ type: ACTIONS.SET_API_PAYLOAD, payload: e.target.checked, subtype: "no_response" })} 
                                />
                                <label className="text-primary" htmlFor="no_response" lang="fr">Sujet en lecture seule, sauf pour son créateur (informations déscendantes)</label>
                            </div>
                        </div>


                        {/* Bouton sauvegarder */}
                        <div className="flex flex-row">
                            <div className="flex flex-col gap-1 mb-5">
                                <button 
                                    className={`rounded-md border border-gray-300 px-2 ${state.apiPayload.title == "" ? "text-gray-100" : "hover:bg-blue-200"} flex flex-row items-center gap-2 py-2`}
                                    onClick={() => handleCreateTopic()}
                                    disabled={state.apiPayload.title == ""}
                                    lang="fr"
                                >
                                    <Save size={24} className="" /> Sauvegarder
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="w-full text-secondary italic flex flex-row items-center gap-2 justify-center" lang="fr">
                        <Hourglass size={16} />
                        La création du premier message sera accessible après la création du sujet
                    </div>
                </>
            ) }
            
        </>
    )
}